<template>
  <ApplicationData :id="id">
    <div
      class="client application-dialog"
      slot-scope="{ application, loading }"
    >
      <!-- Application Dialog -->
      <v-dialog
        @update:returnValue="$emit('close')"
        transition="scale-transition"
        :value="id > 0"
        max-width="900"
        :width="$vuetify.breakpoint.mdAndDown ? '100%' : '70vw'"
        scrollable
        lazy
      >
        <!-- Top Floating Header -->
        <v-fade-transition name="v-fade-transition">
          <v-card
            v-show="showFloatingHeader"
            flat
            class="text-xs-center floating-profile-header"
            :max-width="$vuetify.breakpoint.mdAndDown ? '100%' : '900px'"
          >
            <v-layout row align-start justify-space-around class="mt-2 mx-1">
              <v-flex xs4 class="text-xs-left">
                <v-layout
                  row
                  :wrap="$vuetify.breakpoint.smAndDown"
                  v-if="application.status !== 'Withdrawn'"
                >
                  <v-menu
                    :close-on-content-click="false"
                    max-width="400"
                    max-height="50vh"
                    offset-y
                    bottom
                    right
                    attach
                  >
                    <v-btn
                      slot="activator"
                      class="v-btn--xs"
                      :color="themes.client.light.primaryActionButton"
                    >
                      <v-icon small>mdi-folder</v-icon>
                    </v-btn>
                    <v-card>
                      <ApplicationDocuments
                        v-if="!loading"
                        :application="application"
                      />
                    </v-card>
                  </v-menu>

                  <v-menu
                    v-if="
                      application.questionnaires &&
                      application.questionnaires[0]
                    "
                    :close-on-content-click="false"
                    max-width="400"
                    max-height="50vh"
                    offset-y
                    bottom
                    attach
                  >
                    <v-btn
                      class="v-btn--xs"
                      :color="themes.client.light.primaryActionButton"
                      slot="activator"
                    >
                      <v-icon small>mdi-forum</v-icon>
                    </v-btn>
                    <v-card>
                      <ApplicationQuestionnaire
                        :value="application.questionnaires"
                      />
                    </v-card>
                  </v-menu>
                  <v-menu
                    v-if="
                      application.psychometrics &&
                      application.psychometrics.filter(
                        (p) =>
                          p.show || p.provider != 'Odyssey' || p.aggregate >= 2
                      ).length
                    "
                    :close-on-content-click="false"
                    max-width="400"
                    max-height="50vh"
                    offset-y
                    bottom
                    attach
                  >
                    <v-btn
                      class="v-btn--xs"
                      :color="themes.client.light.primaryActionButton"
                      slot="activator"
                    >
                      <v-icon small>mdi-account-search</v-icon>
                    </v-btn>
                    <v-card>
                      <ApplicationPsychometrics :application="application" />
                    </v-card>
                  </v-menu>

                  <ApplicationsExportSelector
                    v-if="application.opportunity && isManager"
                    :value="[application.id]"
                    :opportunity="application.opportunity"
                    :profile="application.profile"
                    attach
                  >
                    <v-btn
                      slot="activator"
                      :color="themes.client.light.primaryActionButton"
                      class="v-btn--xs"
                    >
                      <v-icon small>mdi-file-pdf-box</v-icon>
                    </v-btn>
                  </ApplicationsExportSelector>

                  <v-tooltip bottom v-if="application && application.id">
                    <v-menu
                      slot="activator"
                      v-model="sharing"
                      :close-on-content-click="false"
                      offset-y
                      bottom
                      attach
                    >
                      <v-btn
                        slot="activator"
                        @click.stop="sharing = !sharing"
                        :color="themes.client.light.primaryActionButton"
                        small
                        :disabled="!canEdit"
                        class="v-btn--xs"
                      >
                        <v-icon small>mdi-share-variant-outline</v-icon>
                      </v-btn>
                      <FeedbackRequestsForm
                        :applications-ids="[application.id]"
                        @close="sharing = false"
                      />
                    </v-menu>
                    <span>Share Candidate</span>
                  </v-tooltip>
                </v-layout>
              </v-flex>

              <v-flex xs4 v-if="application && application.profile">
                <div class="text-xs-center" style="margin-top: -40px">
                  <v-avatar size="80">
                    <img
                      v-if="application.profile && application.profile.avatar"
                      :src="application.profile.avatar"
                      v-img-orientation-changer
                    />
                    <img v-else src="/img/missing/avatars/original.png" />
                  </v-avatar>
                </div>
                <div class="headline">
                  <span v-if="application.profile.firstName">{{
                    application.profile.firstName
                  }}</span>
                  <span v-if="application.profile.lastName"
                    >&nbsp;{{ application.profile.lastName }}</span
                  >
                </div>
                <div class="rating-wrapper">
                  <ApplicationRating
                    :application="application"
                    :edit="canEdit"
                  />
                </div>
              </v-flex>
              <v-flex xs4 v-if="application && application.profile">
                <v-layout row>
                  <v-flex class="text-xs-right pa-0">
                    <v-tooltip bottom>
                      <v-btn
                        icon
                        small
                        :color="themes.client.light.primaryActionButton"
                        slot="activator"
                        @click="copiedToast('email')"
                        v-clipboard:copy="application.profile.email"
                      >
                        <v-icon small>mdi-email</v-icon>
                      </v-btn>
                      <span>
                        {{ application.profile.email }}<br />
                        Click to copy
                      </span>
                    </v-tooltip>
                    <MobileNumberValidator
                      v-if="application.profile.mobileNumber"
                      :value="application.profile.mobileNumber"
                    >
                      <v-tooltip bottom slot-scope="{ validator }">
                        <v-btn
                          icon
                          small
                          :color="themes.client.light.primaryActionButton"
                          slot="activator"
                          @click="copiedToast('mobile')"
                          v-clipboard:copy="validator.mobileNumber"
                        >
                          <v-icon small>mdi-phone</v-icon>
                        </v-btn>
                        <div>
                          Click to copy
                          <div>
                            {{ validator.mobileNumber }}
                          </div>
                        </div>
                      </v-tooltip>
                    </MobileNumberValidator>
                    <v-tooltip bottom>
                      <v-menu offset-y left slot="activator">
                        <v-btn
                          slot="activator"
                          icon
                          small
                          :color="themes.client.light.primaryActionButton"
                        >
                          <v-icon small>mdi-information-variant</v-icon>
                        </v-btn>
                        <v-card>
                          <PersonalDetails
                            :profile="application.profile"
                            :actionItems="application.actionItems"
                          />
                        </v-card>
                      </v-menu>
                      <span> View Personal Information </span>
                    </v-tooltip>

                    <!-- Eligibility Details -->
                    <v-tooltip
                      v-if="
                        application.eligibility &&
                        application.eligibility.eligible === false
                      "
                      bottom
                    >
                      <v-menu offset-y left slot="activator" attach>
                        <v-btn
                          slot="activator"
                          icon
                          small
                          :color="themes.client.light.errorButton"
                        >
                          <v-icon small>mdi-account-alert</v-icon>
                        </v-btn>
                        <v-card>
                          <v-subheader :value="true">
                            Application no longer eligible
                          </v-subheader>
                        </v-card>
                      </v-menu>
                      <span> Application Ineligible </span>
                    </v-tooltip>

                    <!-- Completion Details -->
                    <v-tooltip bottom v-else>
                      <v-menu
                        offset-y
                        left
                        slot="activator"
                        attach
                        v-if="
                          application.actionItems && application.actionItems[0]
                        "
                      >
                        <v-btn
                          small
                          icon
                          slot="activator"
                          :color="themes.client.light.warningButton"
                        >
                          <v-icon small>mdi-account-question</v-icon>
                        </v-btn>
                        <v-card>
                          <ApplicationCompletionDetails
                            :application="application"
                          />
                        </v-card>
                      </v-menu>
                      <v-btn
                        v-else
                        small
                        icon
                        slot="activator"
                        :color="themes.client.light.successButton"
                        @click.stop="viewPdf(application)"
                        :loading="generatingPDF"
                      >
                        <v-icon small>mdi-account-check</v-icon>
                      </v-btn>
                      <span
                        v-if="
                          application.actionItems && application.actionItems[0]
                        "
                      >
                        Application Incomplete
                      </span>
                      <span v-else>View as PDF</span>
                    </v-tooltip>
                  </v-flex>
                </v-layout>
              </v-flex>
            </v-layout>
          </v-card>
        </v-fade-transition>

        <!-- Loading Indicator -->
        <v-card :class="{ 'pt-0': showFloatingHeader }">
          <v-card-text v-if="loading" key="application-loading-indicator">
            <div class="text-xs-center pa-3">
              <v-progress-circular
                size="150"
                indeterminate
                :color="themes.client.light.primary"
              >
                <v-avatar size="140" v-if="application && application.profile">
                  <img
                    v-if="application.profile && application.profile.avatar"
                    :src="application.profile.avatar"
                    v-img-orientation-changer
                  />
                  <img v-else src="/img/missing/avatars/original.png" />
                </v-avatar>
              </v-progress-circular>
              <div class="headline" v-if="application && application.profile">
                <span v-if="application.profile.firstName">{{
                  application.profile.firstName
                }}</span>
                <span v-if="application.profile.lastName"
                  >&nbsp;{{ application.profile.lastName }}</span
                >
              </div>
            </div>
          </v-card-text>
          <!-- Scrollable Application -->
          <v-card-text
            v-else
            key="application-details"
            id="profile-container"
            v-scroll:#profile-container="onScroll"
            style="overflow-y: scroll; overflow-x: hidden; margin-bottom: 3rem"
            :style="{ 'max-height': profileHeight }"
          >
            <Application :application="application" />
          </v-card-text>
          <!-- Bottom Toolbar -->
          <v-card-actions
            class="pa-0"
            v-if="!loading && canEdit && application.status !== 'Withdrawn'"
          >
            <CandidateQuickActionToolbar
              :candidate="application"
              @done="$emit('close')"
            />
          </v-card-actions>
        </v-card>
      </v-dialog>
    </div>
  </ApplicationData>
</template>

<script>
// Components
import Application from "@/components/client/applications/Application";
import ApplicationCompletionDetails from "@/components/client/applications/ApplicationCompletionDetails";
import ApplicationData from "@/components/client/applications/ApplicationData";
import ApplicationDocuments from "@/components/client/applications/ApplicationDocuments";
import ApplicationPsychometrics from "@/components/client/applications/ApplicationPsychometrics";
import ApplicationQuestionnaire from "@/components/client/applications/ApplicationQuestionnaire";
import ApplicationRating from "@/components/client/applications/ApplicationRating";
import ApplicationsExportSelector from "@/components/client/applications/ApplicationsExportSelector";
import CandidateQuickActionToolbar from "@/components/client/application_tracker/CandidateQuickActionToolbar";
import FeedbackRequestsForm from "@/components/client/feedback_requests/FeedbackRequestsForm";
import MobileNumberValidator from "@/components/candidate/MobileNumberValidator";
import PersonalDetails from "@/components/client/candidate/profile/PersonalDetails";
import Vue from "vue";
import { generatedDocumentDownloads } from "@/mixins/generatedDocumentDownloads.js";

export default {
  mixins: [generatedDocumentDownloads],

  components: {
    Application,
    ApplicationCompletionDetails,
    ApplicationData,
    ApplicationDocuments,
    ApplicationPsychometrics,
    ApplicationQuestionnaire,
    ApplicationRating,
    ApplicationsExportSelector,
    CandidateQuickActionToolbar,
    FeedbackRequestsForm,
    MobileNumberValidator,
    PersonalDetails,
  },
  props: { id: Number },
  data() {
    return {
      // loading: false,
      showFloatingHeader: false,
      width: 750,
      canEdit: true,
      sharing: false,
      generatingPDF: false,
    };
  },

  computed: {
    isManager() {
      return this.$store.getters["client/opportunities/currentIsManager"]();
    },

    profileHeight() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
          return "80vh";
        case "sm":
          return "80vh";
        case "md":
          return "82vh";
        case "lg":
          return "82vh";
        case "xl":
          return "82vh";
        default:
          return "80vh";
      }
    },
  },
  methods: {
    onScroll(e) {
      this.showFloatingHeader = e.target.scrollTop > 300;
    },

    copiedToast(value) {
      this.$store.dispatch("snackbarShow", `Copied ${value}.`);
    },

    async viewPdf(application) {
      this.generatingPDF = true;
      await this.handleGeneratedDocumentRequest(
        this.$http.get(
          `${this.$root.$api}/client/opportunities/${application.opportunity.id}/applications/${application.id}.pdf`,
          { responseType: "blob" }
        ),
        {
          openInline: true,
          pendingMessage:
            "Your application document has been requested. You will receive an email with a download link.",
          errorMessage: `PDF generation error, please let us know: Application ID ${application.id}.`,
        }
      );

      this.generatingPDF = false;
    },
  },
};
</script>

<style>
div.client.application-dialog div.rating-wrapper fieldset {
  position: relative;
}

.client.application-dialog nav div.v-toolbar__content {
  padding: 0;
}

.client.application-dialog .floating-profile-header {
  position: fixed;
  top: 6vh;
  width: 70vw;
  z-index: 10001;
  left: 50%;
  transform: translateX(-50%);
}

.client.application-dialog .floating-profile-header .v-btn--xs,
.client.application-dialog .floating-profile-header .v-btn--xs::before {
  min-width: 0;
  width: 44px;
  height: 28px;
}
</style>
